"use client";

/**
 * NodeFleetLive — live, AGGREGATE-ONLY figures for the dedicated indexer fleet.
 *
 * Reads the same two public endpoints the askfinz.ai status page uses, but
 * renders only pool-wide totals.
 *
 * DO NOT render anything from `pool.nodes[]`, `escalation_detail`, or any
 * per-node field. Those carry egress addresses, hardware names and the exact
 * sites a node is reading — publishing them on the marketing site would leak
 * the fleet's egress identity and a map of what it crawls. Totals only.
 */

import { useEffect, useState } from "react";
import { Cpu, Database, Globe, HardDrive, Library, Server, Sparkles } from "lucide-react";
import { fmtCount } from "@/lib/format-stat";
import { StatTileGrid } from "@/components/marketing/stat-tile";
import { CORPORA } from "@/lib/corpora";

type Pool = {
  pool?: {
    nodes_online?: number;
    cores?: number;
    indexed?: number;
    mem_total_gb?: number;
    bandwidth?: { recv?: number };
    embed_pool?: { nodes?: number; eps?: number };
  };
};

/**
 * `standalone` — the only stats band on the page (/indexer_node). Leads with
 * the index total, because that is the argument for lending a machine.
 *
 * `fleet` — the page already shows the index total elsewhere (/indexer, where
 * SearchPerformanceLive renders the very same `pages_indexed` field). Showing
 * it twice under two labels reads as two different numbers, so this variant
 * drops it and shows pooled memory instead.
 */
export type Variant = "standalone" | "fleet";

const POOL_URL = "https://extension.askfinz.ai/api/v1/search/pool/analytics";

/** 11_263_805 → "11.3M" */
/* Local `compact()` replaced by the shared formatter — it fell back to toLocaleString() above
   10k, so a seven-digit figure printed in full with commas. */
const compact = fmtCount;

/** bytes → "6.0 TB" */
function bytes(n?: number): string {
  if (n == null || !isFinite(n)) return "—";
  const tb = n / 1_000_000_000_000;
  /* Non-breaking space: the unit belongs to the number, and these tiles are narrow. */
  if (tb >= 1) return `${tb.toFixed(1)} TB`;
  return `${Math.round(n / 1_000_000_000)} GB`;
}

export function NodeFleetLive(
  { variant = "standalone", initial }: { variant?: Variant; initial?: Pool["pool"] | null } = {},
) {
  /* Seeded by the server (see live-stats.server.tsx) so the figure is in the HTML on first
     paint rather than replacing a dash after hydration. The client fetch below stays: the page
     is prerendered on a 5-minute timer, and this is what keeps a cached copy current. */
  const [pool, setPool] = useState<Pool["pool"] | null>(initial ?? null);
  const [live, setLive] = useState(false);
  /* All-time bandwidth, kept OUT of `pool` on purpose — see the fetch below. */
  const [recv, setRecv] = useState<number | null>(null);
  const [pages, setPages] = useState<number | null>(null);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const pr = await fetch(POOL_URL, { cache: "no-store" });
        if (pr.ok) {
          const j = (await pr.json()) as Pool;
          if (on && j?.pool) {
            setPool(j.pool);
            setLive(true);
          }
        }

        /* `pool.bandwidth` IS NOT AN ALL-TIME FIGURE and must not be shown as one. It sums the
           counters of the nodes online at that instant, so it drops when a machine leaves — the
           opposite of what "web pulled down" claims. Measured 16 September 2026: 6.8 TB from the
           pool against a true 113.6 TB. The dedicated endpoint reads the accumulated counter,
           which only ever rises and is the same series the live timeline chart draws. */
        const br = await fetch("/api/index/bandwidth", { cache: "no-store" });
        if (br.ok) {
          const b = (await br.json()) as { recv: number | null };
          if (on) setRecv(b?.recv ?? null);
        }

        /* Same reasoning as the bandwidth call above: the page count that survives a node leaving
           lives on the storage feed, not in the pool payload. */
        const sr = await fetch("/api/index/storage", { cache: "no-store" });
        if (sr.ok) {
          const st = (await sr.json()) as { web?: { pages?: number } };
          if (on && typeof st?.web?.pages === "number") setPages(st.web.pages);
        }
      } catch {
        /* transient — keep the last values on screen */
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  // The standalone lead was "Pages searchable" — an index total. Dropped while the index is still
  // being built: a running total is the one figure that invites a comparison against an established
  // index, and it dates badly wherever the page gets cached or summarised.
  //
  // Pooled memory replaces it in both variants. That describes the MACHINERY rather than how much
  // has been filed, which is the line held across every stat block on these pages: fleet capacity
  // and fleet work are fair game, index contents are not. "Pages read by the fleet" below stays for
  // that reason — it counts work done, including re-reads, not what the index currently holds.
  const lead =
    variant === "standalone"
      ? {
          icon: Database,
          value: pool?.mem_total_gb != null ? `${Math.round(pool.mem_total_gb)} GB` : "—",
          label: "Memory pooled",
          sub: "across every machine reading",
        }
      : {
          icon: Database,
          value: pool?.mem_total_gb != null ? `${Math.round(pool.mem_total_gb)} GB` : "—",
          label: "Memory pooled",
          sub: "across the machines",
        };

  const stats = [
    lead,
    {
      icon: Server,
      value: pool?.nodes_online != null ? String(pool.nodes_online) : "—",
      label: "Machines online",
      sub: "dedicated to reading",
    },
    {
      icon: Cpu,
      value: pool?.cores != null ? String(pool.cores) : "—",
      label: "Processor cores",
      sub: "spare capacity, pooled",
    },
    {
      icon: HardDrive,
      /* THE DURABLE COUNT, NOT THE POOL'S. `pool.indexed` sums the nodes online at that instant,
         so it falls when a machine leaves — the same defect that had this strip publishing 6.8 TB
         against a true 113 TB, fixed for bandwidth and left here. Sitting beside an all-time byte
         total it also read as all-time, which it was not. `web.pages` is the durable figure the
         indexing dashboard reports, so the two tiles are now on the same basis. */
      value: compact(pages ?? undefined),
      label: "Pages in the index",
      /* SCOPE, NOT DECORATION. `web.pages` counts the web-page index and nothing else — the
         backend derives it that way on purpose, because dividing fleet-wide bytes by web pages
         would charge patents, standards and clinical trials to the page count and roughly double
         it. Beside a strip of fleet-wide totals the old sub-line ("read, cleaned and filed") let
         this read as everything askFinz holds, which it is not: every subject collection sits
         outside this number. Saying "web pages" is the whole fix. */
      sub: "web pages, read and filed",
    },
    {
      icon: Library,
      /* Static because it is a fact about the site, not a reading of the fleet: it is the same
         list /collections renders, from the same module, so the two cannot disagree. The fleet
         total those collections add up to is deliberately NOT published — it is a disclosure
         about what is held privately — but their EXISTENCE is already public, and a strip that
         omitted them implied the web page count was the whole index. */
      value: String(CORPORA.length),
      label: "Subject collections",
      sub: "filed beside the web pages",
    },
    {
      icon: Globe,
      value: bytes(recv ?? undefined),
      label: "Web pulled down",
      sub: "read, cleaned, filed",
    },
    {
      icon: Sparkles,
      value: pool?.embed_pool?.eps != null ? `${Math.round(pool.embed_pool.eps)}/s` : "—",
      label: "Shared card throughput",
      sub: `across ${pool?.embed_pool?.nodes ?? "—"} shared graphics cards`,
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <span
          className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="kicker">{live ? "Live · from the fleet right now" : "Connecting…"}</span>
      </div>
      {/* THIS STRIP IS WHERE THE COLUMN COUNT HURT MOST. It sits in an 8-of-12 column on
          /web-index, so `lg:grid-cols-6` left each tile 120px wide — 86px inside the padding —
          which wrapped every label onto two lines and stood each card 204px tall. It is also used
          on /indexer_node and /indexer_docker beside a max-w-2xl block, where six across is a
          different shape of wrong. The grid now sizes itself from whatever container it lands in;
          see `.stat-tile-grid`. */}
      <StatTileGrid items={stats} />
    </div>
  );
}
